import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, Ban, CheckCircle2, Trash2, LogIn, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { owner } from "@/lib/owner-api";
import { formatDate, formatNumber } from "@/lib/format";

export default function OwnerMerchants() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [planChange, setPlanChange] = useState({});

  const fetchList = async (q = "") => {
    setLoading(true);
    try { setRows(await owner.listMerchants(q)); } catch (err) { console.warn("Merchants load failed:", err); }
    setLoading(false);
  };
  useEffect(() => { fetchList(); }, []);

  const doSearch = (e) => { e.preventDefault(); fetchList(search); };

  const suspend = async (m, suspended) => {
    if (!window.confirm(`${suspended ? "Suspend" : "Aktifkan kembali"} merchant "${m.business_name}"?`)) return;
    try {
      await owner.updateMerchant(m.id, { suspended });
      toast.success(suspended ? "Merchant disuspend" : "Merchant diaktifkan");
      fetchList(search);
    } catch { toast.error("Gagal"); }
  };

  const changePlan = async (m, plan) => {
    try {
      await owner.updateMerchant(m.id, { subscription_plan: plan });
      toast.success(`Plan diubah ke ${plan}`);
      fetchList(search);
    } catch { toast.error("Gagal"); }
  };

  const impersonate = async (m) => {
    if (!window.confirm(`Login sebagai "${m.business_name}"? Aktivitas Anda akan tercatat di audit log.`)) return;
    try {
      const { token } = await owner.impersonate(m.id);
      localStorage.setItem("oc_token", token);
      localStorage.setItem("oc_merchant", JSON.stringify(m));
      toast.success("Impersonating merchant...");
      setTimeout(() => window.location.href = "/dashboard", 400);
    } catch { toast.error("Impersonate gagal"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`HAPUS PERMANEN merchant "${m.business_name}"? Semua data akan hilang. Tidak bisa dibatalkan.`)) return;
    try {
      await owner.deleteMerchant(m.id);
      toast.success("Merchant dihapus");
      fetchList(search);
    } catch { toast.error("Gagal"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Merchant Management</h1>
        <p className="text-sm text-gray-500 mt-1">Kelola semua merchant, upgrade plan, suspend, atau impersonate untuk troubleshooting.</p>
      </div>

      <form onSubmit={doSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
          <Input placeholder="Cari nama toko, email, atau owner..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-gray-900 border-gray-800 text-white" data-testid="owner-merchant-search" />
        </div>
        <Button type="submit" variant="outline" className="border-gray-800 bg-gray-900 text-white hover:bg-gray-800">Cari</Button>
      </form>

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        {loading ? <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-emerald-400" /></div>
          : rows.length === 0 ? (
            <div className="py-16 text-center">
              <Building2 className="mx-auto text-gray-700" size={56} />
              <h3 className="mt-4 font-heading text-lg font-semibold text-white">Belum ada merchant</h3>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-800/50 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-800">
                  <tr>
                    <th className="px-4 py-3">Merchant</th>
                    <th className="px-4 py-3">Owner</th>
                    <th className="px-4 py-3">Customers</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Joined</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {rows.map((m) => (
                    <tr key={m.id} className="hover:bg-gray-800/40" data-testid={`merchant-row-${m.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-white">{m.business_name}</div>
                        <div className="text-xs text-gray-500">{m.email}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-300">{m.owner_name}</td>
                      <td className="px-4 py-3 font-bold text-emerald-400">{formatNumber(m.customer_count || 0)}</td>
                      <td className="px-4 py-3">
                        <Select value={m.subscription_plan} onValueChange={(v) => changePlan(m, v)}>
                          <SelectTrigger className="h-8 w-28 bg-gray-800 border-gray-700 text-white capitalize" data-testid={`plan-select-${m.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="starter">Starter</SelectItem>
                            <SelectItem value="growth">Growth</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                            <SelectItem value="business">Business</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        {m.suspended ? (
                          <Badge className="bg-red-900/30 text-red-400 border border-red-800">Suspended</Badge>
                        ) : m.onboarded ? (
                          <Badge className="bg-emerald-900/30 text-emerald-400 border border-emerald-800">Active</Badge>
                        ) : (
                          <Badge className="bg-amber-900/30 text-amber-400 border border-amber-800">Onboarding</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{formatDate(m.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" className="h-8 border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700" onClick={() => impersonate(m)} data-testid={`impersonate-${m.id}`} title="Login sebagai merchant">
                            <LogIn size={14} />
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700" onClick={() => suspend(m, !m.suspended)} data-testid={`suspend-${m.id}`} title={m.suspended ? "Aktifkan" : "Suspend"}>
                            {m.suspended ? <CheckCircle2 size={14} className="text-emerald-400" /> : <Ban size={14} className="text-amber-400" />}
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 border-red-900 bg-red-950/30 text-red-400 hover:bg-red-900/50" onClick={() => remove(m)} data-testid={`delete-merchant-${m.id}`} title="Hapus permanen">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}
